import { Component, OnInit, ViewChild } from '@angular/core';
import { AccessLevel } from '../../../common/enums/access-level.enum';
import { CommonService } from '../../../shared/services/common.service';
import { FileUploadConstants } from '../../constants/file-upload.constants';
import { TabsetComponent } from 'ngx-bootstrap';
import { CustomerService } from '../../services/customer.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NotificationType } from '../../enums/notification-types.enum';
import { TranslateService } from '@ngx-translate/core';
import { BatchProcessingService } from '../../services/batch-processing.service';
import { UserInfo } from '../../../shared/interface/user-info.interface';
import { CacheService } from '../../../shared/services/cache.service';
import { BusinessEntity } from '../../interface/businessentity';
import { CacheKey } from '../../../shared/constants/cachekey.constants';

@Component({
    selector: 'app-file-uploads',
    templateUrl: '../../templates/template1/views/file-uploads/file-uploads.component.html',
    styleUrls: ['../../templates/template1/themes/default/less/file-uploads.component.less']
})

export class FileUploadsComponent implements OnInit {
    activeTab = 1;
    categoryPermissions = FileUploadConstants.CATEGORY_PERMISSIONS;
    categories = [];
    tempTemplates = [];
    templates = [];
    templateInfo = null;
    categoryId = 0;
    templateId = 0;
    categoryIdentifier = '';
    @ViewChild('fileUploadTabs') fileUploadTabs: TabsetComponent;
    fileUploadsForm: FormGroup;
    categoryName: string;
    activityLogs = [];
    userInfo: UserInfo;
    businessEntities: BusinessEntity[] = [];
    countries: any;
    operatingCountry: number;

    constructor(private _commonService: CommonService,
        private _customerService: CustomerService,
        private _batchService: BatchProcessingService,
        private _formbuilder: FormBuilder,
        private _translatePipe: TranslateService,
        private _cacheService: CacheService
        ) {}

    ngOnInit(): void {
        this.createFileUploadsForm();
        this.loadTemplates();
        this.loadCategories();
        this.getActivityLogs();
        this.userInfo = this._cacheService.get(CacheKey.UserInfo);
        this.getBusinessEntities();
    }

    /**
     * To Create file uploads form
     */
    createFileUploadsForm(): void {
        this.fileUploadsForm = this._formbuilder.group({
            category: ['', Validators.required],
            template: ['', Validators.required],
            operatingCountry: ['', Validators.required]
        });
    }

    /**
     * To Load categories from services
     */
    loadCategories(): void {
        this._customerService.getFileUploadCategories().subscribe(response => {
            if (response && response.length > 0) {
                this.setPermissions(response);
            }
        }, error => {
            this.categories = [];
            const message: any = this._translatePipe.get('fileuploads.Something Went Wrong, Please try again after some time');
            this._commonService.createNotification('', message.value, NotificationType.ERROR);
        });
    }

    /**
     * To Load templates from service
     */
    loadTemplates(): void {
        this._customerService.getFileUploadTemplates().subscribe(response => {
            if (response && response.length > 0) {
                this.templates = JSON.parse(JSON.stringify(response));
                this.tempTemplates = JSON.parse(JSON.stringify(response));
            }
        }, error => {
            this.templates = [];
            const message: any = this._translatePipe.get('fileuploads.Something Went Wrong, Please try again after some time');
            this._commonService.createNotification('', message.value, NotificationType.ERROR);
        });
    }

    /**
     * To Load Activity logs from service
     */
    getActivityLogs(): void {
        this._batchService.getActivityLog().subscribe((response) => {
            if (response && response.length > 0) {
                this.activityLogs = response;
            } else {
                this.activityLogs = [];
            }
        }, error => {
            this.activityLogs = [];
        });
    }

    /**
     * To Load the categories based on the permissions
     */
    setPermissions(categories: any): void {
        let tempData = null;
        let tempCategories = [];
        if (categories && categories.length > 0) {
            categories.forEach(category => {
                tempData = this.categoryPermissions.find(permission => permission.identifier === category.categoryIdentifier);
                category.permissionId = tempData && tempData.permissionId ? tempData.permissionId : null;
            });
            tempCategories = categories.filter(category => this._commonService.checkPermissionByAccessLevel(JSON.parse('[' + category.permissionId + ']'), AccessLevel.Customer_Service));
            tempCategories = tempCategories && tempCategories.length > 0 ? 
                tempCategories.filter(temp => (temp.categoryIdentifier === this.categoryPermissions[1].identifier)) : []; //Filterd Only Recognition (or) LocalRecognition Category
            this.categories = JSON.parse(JSON.stringify(tempCategories));
        }
    }

    /**
     * To Set the default values when change the category
     */
    changeCategory(categoryId: number): void {
        this.categoryId = categoryId ? +categoryId : null;
        const category =  this.categories.find(category => category.id === this.categoryId);
        this.fileUploadsForm.controls['operatingCountry'].setValue('');
        this.categoryIdentifier = category && category.categoryIdentifier ? category.categoryIdentifier : null;
        this.categoryName = category && category.categoryName ? category.categoryName : '';
        if (this.categoryIdentifier === this.categoryPermissions[1].identifier) {
          const templates = this.tempTemplates.filter(template => template.categoryId === this.categoryId);
          this.templates = JSON.parse(JSON.stringify(templates));
          this.templateInfo = null;
          this.fileUploadsForm.controls['template'].setValue('');
          this.templateId = 0;
        }
    }

    /**
     * To Set the template info when change template
     */
    changeTemplate(templateId: number): void {
        this.templateId = templateId ? +templateId : null;
        this.templateInfo =  this.tempTemplates.find(tempTemplate => tempTemplate.id === this.templateId);
    }

    /**
     * To Get All Business Entities
     */
    getBusinessEntities() {
        this._customerService.getAllBusinessEntities().subscribe(response => {
            if (response) {
                this.businessEntities = response;
                this.getAllowedCountries();
                }
        });
    }

    /**
     * To Get User Allowed Countries
     *
     */
    getAllowedCountries() {
        let countryIds: any = [];
        if (!this.userInfo.hasSystemRole) {
            if (this.userInfo.allowedOperatingCountries) {
                countryIds = this.userInfo.allowedOperatingCountries.split(',');
                if (this.userInfo.privileges.departments) {
                    countryIds.push.apply(countryIds, this.userInfo.privileges.departments.map(function (a) { return a.allowedOperatingCountries; }));
                }
                countryIds = countryIds ? countryIds : [];
                if (countryIds.length > 0) {
                    const ids = countryIds;
                    this.countries = this.businessEntities.filter(function (item) {
                        return ids.indexOf(item.id + '') !== -1;
                    });
                }
            } else {
                this.countries = this.businessEntities;
            }
        } else {
            this.countries = this.businessEntities;
        }
        this.countries.sort(function (item1, item2) {
            const country1 = item1.name.toLowerCase(), country2 = item2.name.toLowerCase();
            if (country1 < country2) {
                return -1;
            }
            if (country1 > country2) {
                return 1;
            }
            return 0;
        });
    }

    operatingCountryChange(id: any): void {
        this.operatingCountry = id;
    }
}



// WEBPACK FOOTER //
// ./src/app/customerservice/components/file-uploads/file-uploads.component.ts